'use client';

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaMapMarkerAlt } from 'react-icons/fa';

const CoverageSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Central brand color for a unified, professional look
  const brandColor = "#3b82f6";

  const areas = useMemo(
    () => [
      {
        id: "doha",
        title: "Doha",
        description: "Central Doha, West Bay, Corniche, and surrounding areas with construction helper supply Doha.",
        tag: "Capital City"
      },
      {
        id: "lusail",
        title: "Lusail",
        description: "Lusail City, Marina District, and all Lusail development projects with skilled workers in Lusail.",
        tag: "Modern Hub"
      },
      {
        id: "the-pearl",
        title: "The Pearl",
        description: "The Pearl-Qatar, Porto Arabia, and all Pearl development areas with certified tradesmen Qatar.",
        tag: "Residential"
      },
      {
        id: "industrial",
        title: "Industrial Areas",
        description: "All industrial zones and manufacturing facilities with industrial painting Doha and MEP specialists.",
        tag: "Heavy Industry"
      },
      {
        id: "west-bay",
        title: "West Bay",
        description: "West Bay business district and commercial buildings with commercial painting services Qatar.",
        tag: "Business"
      },
      {
        id: "all-qatar",
        title: "All Qatar",
        description: "Complete coverage across Qatar with staff transportation services and 15-seater worker bus fleets.",
        tag: "National"
      },
    ],
    []
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section
      style={{
        padding: '100px 20px',
        background: '#ffffff', // Clean white background to match Services
        position: 'relative',
        overflow: 'hidden',
      }}
      aria-labelledby="coverage-title"
    >
      {/* Background decoration consistent with your Service component */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '600px',
          height: '600px',
          background: `radial-gradient(circle, ${brandColor}05 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Header Section */}
      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={{
          hidden: { opacity: 0, y: -30 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
        }}
        style={{
          maxWidth: '1200px',
          margin: '0 auto 80px',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: `${brandColor}15`,
            borderRadius: '20px',
            marginBottom: '20px',
            border: `1px solid ${brandColor}25`,
          }}
        >
          <span style={{ fontSize: '12px', fontWeight: 600, color: brandColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Our Reach
          </span>
        </div>

        <h2
          id="coverage-title"
          style={{
            fontSize: 'clamp(32px, 6vw, 48px)',
            fontWeight: 900,
            color: '#111827',
            marginBottom: '16px',
            letterSpacing: '-1px',
            lineHeight: 1.2,
          }}
        >
          Serving <span style={{ color: brandColor }}>All Qatar</span>
        </h2>

        <p
          style={{
            fontSize: '18px',
            color: '#6b7684',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.8',
            fontWeight: 500,
          }}
        >
          We provide immediate manpower mobilization across all major zones with skilled professionals ready for deployment.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          <div style={{ width: '40px', height: '4px', background: brandColor, borderRadius: '2px' }} />
          <div style={{ width: '12px', height: '4px', background: brandColor, opacity: 0.3, borderRadius: '2px' }} />
        </div>
      </motion.div>

      {/* Coverage Grid */}
      <motion.div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px',
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
        }}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
      >
        {areas.map((area) => (
          <CoverageCard key={area.id} area={area} variants={cardVariants} brandColor={brandColor} />
        ))}
      </motion.div>
    </section>
  );
};

const CoverageCard = ({ area, variants, brandColor }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <motion.article
      variants={variants}
      whileHover={{ y: -10 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: '#ffffff',
        border: `1px solid ${isHovered ? brandColor + '40' : '#f1f5f9'}`,
        borderRadius: '24px',
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        boxShadow: isHovered
          ? `0 25px 50px ${brandColor}10, 0 8px 16px rgba(0,0,0,0.03)`
          : '0 4px 12px rgba(0, 0, 0, 0.02)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div 
          style={{ 
            width: '48px', 
            height: '48px', 
            background: isHovered ? brandColor : `${brandColor}10`, 
            borderRadius: '12px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: isHovered ? '#fff' : brandColor,
            transition: 'all 0.3s ease'
          }}
        >
          <FaMapMarkerAlt size={20} />
        </div>
        <span style={{ 
          fontSize: '10px', 
          fontWeight: 800, 
          textTransform: 'uppercase', 
          letterSpacing: '1px', 
          color: isHovered ? brandColor : '#64748b',
          background: isHovered ? `${brandColor}10` : '#f8fafc',
          padding: '6px 12px',
          borderRadius: '20px',
          transition: 'all 0.3s ease'
        }}>
          {area.tag}
        </span>
      </div>

      <h3
        style={{
          fontSize: '22px',
          fontWeight: 800,
          color: '#111827',
          marginBottom: '12px',
          letterSpacing: '-0.5px',
        }}
      >
        {area.title}
      </h3>

      <p
        style={{
          fontSize: '15px',
          color: '#64748b',
          lineHeight: '1.6',
          marginBottom: '24px',
          flex: 1,
        }}
      >
        {area.description}
      </p>

      <motion.button
  // The WhatsApp Click Action
  onClick={() => window.open('https://wa.me/97455994434', '_blank')}
  
  // Interaction Animations
  whileHover={{ 
    scale: 1.02,
    backgroundColor: brandColor, // Animates background automatically
    color: "#ffffff",            // Animates text color automatically
    borderColor: brandColor      // Animates border automatically
  }}
  whileTap={{ scale: 0.98 }}
  
  style={{
    padding: '12px 20px',
    background: 'transparent',
    color: '#111827',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    width: '100%',
  }}
>
  Inquire for {area.title}
</motion.button>

      {/* Subtle bottom accent line */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '32px',
          right: '32px',
          height: isHovered ? '4px' : '0px',
          background: brandColor,
          borderRadius: '4px 4px 0 0',
          transition: 'height 0.3s ease',
        }}
      />
    </motion.article>
  );
};

export default CoverageSection;